<template>
  <HoppSmartModal
    v-if="show"
    dialog
    :title="`Manage Access: ${user?.email}`"
    @close="closeModal"
  >
    <template #body>
      <div class="flex flex-col space-y-4">
        <div v-if="loading" class="flex justify-center p-4">
          <HoppSmartSpinner />
        </div>
        <div v-else class="divide-y divide-dividerLight">
          <div
            v-for="collection in collectionsList"
            :key="collection.id"
            class="flex items-center justify-between py-3"
          >
            <span class="text-body font-semibold text-secondaryDark">
              {{ collection.title }}
            </span>
            <HoppSmartToggle
              :on="hasAccess(collection.id)"
              @change="toggleAccess(collection.id)"
            />
          </div>
          <div
            v-if="collectionsList.length === 0"
            class="flex items-center justify-center p-4 text-secondaryLight"
          >
            No collections found in this team.
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end">
        <HoppButtonSecondary
          label="Close"
          outline
          @click="closeModal"
        />
      </div>
    </template>
  </HoppSmartModal>
</template>

<script setup lang="ts">
import { computed, watch } from "vue"
import * as E from "fp-ts/Either"
import { useGQLQuery } from "~/composables/graphql"
import { useToast } from "@composables/toast"
import {
  RootCollectionsOfTeamDocument,
  RootCollectionsOfTeamQuery,
  RootCollectionsOfTeamQueryVariables,
  RootCollectionsOfUserInTeamDocument,
  RootCollectionsOfUserInTeamQuery,
  RootCollectionsOfUserInTeamQueryVariables,
} from "~/helpers/backend/graphql"
import {
  grantCollectionAccess,
  revokeCollectionAccess,
} from "~/helpers/backend/mutations/TeamCollection"

const props = defineProps<{
  show: boolean
  teamID: string
  user: {
    userID: string
    email: string
  } | null
}>()

const emit = defineEmits<{
  (e: "hide-modal"): void
}>()

const toast = useToast()

const rootCollections = useGQLQuery<
  RootCollectionsOfTeamQuery,
  RootCollectionsOfTeamQueryVariables,
  ""
>({
  query: RootCollectionsOfTeamDocument,
  variables: {
    teamID: props.teamID,
  },
  defer: true,
})

const userCollections = useGQLQuery<
  RootCollectionsOfUserInTeamQuery,
  RootCollectionsOfUserInTeamQueryVariables,
  ""
>({
  query: RootCollectionsOfUserInTeamDocument,
  variables: {
    teamID: props.teamID,
    userUid: props.user?.userID ?? "",
  },
  defer: true,
})

const loading = computed(() => {
  return rootCollections.loading || userCollections.loading
})

const collectionsList = computed(() => {
  if (E.isLeft(rootCollections.data.value)) return []
  return rootCollections.data.value.right.rootCollectionsOfTeam || []
})

const hasAccess = (collectionID: string) => {
  if (E.isLeft(userCollections.data.value)) return false
  const list =
    userCollections.data.value.right.rootCollectionsOfUserInTeam || []
  return list.some((c: any) => c.id === collectionID)
}

const toggleAccess = async (collectionID: string) => {
  if (!props.user) return

  const currentAccess = hasAccess(collectionID)
  if (currentAccess) {
    const res = await revokeCollectionAccess(collectionID, props.user.userID)()
    if (E.isLeft(res)) {
      toast.error("Failed to revoke collection access")
    } else {
      toast.success("Collection access revoked")
      userCollections.execute({
        teamID: props.teamID,
        userUid: props.user.userID,
      })
    }
  } else {
    const res = await grantCollectionAccess(collectionID, props.user.userID)()
    if (E.isLeft(res)) {
      toast.error("Failed to grant collection access")
    } else {
      toast.success("Collection access granted")
      userCollections.execute({
        teamID: props.teamID,
        userUid: props.user.userID,
      })
    }
  }
}

const closeModal = () => {
  emit("hide-modal")
}

watch(
  () => props.show,
  (show) => {
    if (show && props.teamID && props.user?.userID) {
      rootCollections.execute({ teamID: props.teamID })
      userCollections.execute({
        teamID: props.teamID,
        userUid: props.user.userID,
      })
    }
  }
)
</script>
